/**
 * Speaker profiles -- group 08 (horn loading, plasma tweeters and single-driver
 * wide-band designs).
 *
 * These are all high-efficiency, low-part-count designs, so the usual
 * differentiators of a conventional multi-way -- crossover count, driver
 * material, cabinet bracing -- barely apply. What separates them instead is how
 * they get their sensitivity (acoustic transformation versus driver efficiency),
 * what happens at the top where a whizzer cone or a plasma takes over, and how
 * the low end is loaded. Four of the eight have no crossover in the audio band
 * at all, which the model records literally: zero crossover sections, hence no
 * crossover phase rotation and no inter-driver arrival error.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_08: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Acapella Audio Arts',
    model: 'Violon',
    price: usd(75000, 'APPROXIMATE', 2020, 'Approximate US retail per pair. Acapella prices in EUR and by dealer configuration, so this is a bracket rather than a quoted MSRP.'),
    year: 2005,
    country: 'Germany',
    speakerType: 'horn',
    enclosure: 'horn-loaded',
    drivers: ['plasma', 'compression-driver', 'dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 93,
    nominalOhms: 8,
    minOhms: 5.5,
    bass: [30, 24],
    hfExtension: 50000,
    crossovers: [[700, 12], [5000, 6]],
    responseTilt: [[20, 0], [45, 0.6], [90, -0.4], [300, -0.8], [700, 0.7], [1500, 1.2], [3000, 0.5], [6000, -0.2], [12000, 0.4], [20000, 0.9]],
    resonances: [[135, -34, 11], [610, -40, 15]],
    coverageAngleDeg: 55,
    recommendedWatts: [10, 200],
    dimensionsMm: [1420, 560, 700],
    massKg: 120,
    thdPercent: 0.5,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#e8c07d',
    tags: ['ion-tweeter', 'spherical-horn', 'three-way', 'flagship'],
    notes:
      'Above 5 kHz there is no diaphragm at all: the ion tweeter modulates a plasma discharge directly, so there is no moving mass, no suspension and no stored energy to release after the signal stops. The model reflects that as an HF limit of 50 kHz with a first-order handover and no rise in distortion with level in the top octaves, which is the opposite of the spherical midrange horn below it, where air nonlinearity in the throat makes the second harmonic climb steadily with SPL. The 700 Hz horn is wide but not constant-directivity, so on-axis response and power response diverge through the presence region.',
    provenanceNote:
      'Driver complement including the ion tweeter, horn loading, sensitivity and nominal impedance are from Acapella Audio Arts published material. Crossover points, bass extension, minimum impedance, cabinet resonances and the voicing offsets are ESTIMATED or MODELLED. The negligible high-frequency distortion of the plasma section is inferred from the absence of a diaphragm, not measured.',
  }),

  makeSpeaker({
    mfr: 'Lansche Audio',
    model: 'No.4.1',
    price: usd(45000, 'APPROXIMATE', 2018, 'Approximate US retail per pair; priced in EUR at source.'),
    year: 2011,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['plasma', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 92,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [28, 23],
    hfExtension: 45000,
    portTuningHz: 27,
    crossovers: [[300, 12], [2800, 12]],
    responseTilt: [[20, 0.4], [50, 0.8], [120, -0.5], [400, -0.3], [1000, 0.2], [2800, -0.6], [5000, 0.9], [10000, 1.1], [20000, 1.4]],
    resonances: [[160, -30, 9], [430, -36, 13]],
    coverageAngleDeg: 80,
    recommendedWatts: [30, 300],
    dimensionsMm: [1290, 320, 480],
    massKg: 85,
    thdPercent: 0.35,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#7fb3d5',
    tags: ['corona-plasma', 'reflex-loaded', 'low-crossover-tweeter'],
    notes:
      'The instructive contrast with the Acapella entry: same plasma principle at the top, completely different loading everywhere else. This is a conventional reflex box, so its 92 dB comes from driver efficiency rather than from acoustic transformation, and the modelled impedance is a normal ported alignment with a saddle at 27 Hz instead of a horn impedance peak. The corona tweeter crosses low at 2.8 kHz, so the plasma covers the whole top three octaves and the usual dome breakup peak between 20 and 30 kHz is simply absent -- hence the gently rising, entirely smooth top end in the voicing rather than a peak-and-dip pair.',
    provenanceNote:
      'The corona plasma tweeter, three-way configuration and reflex loading are from Lansche Audio published material. Sensitivity, nominal and minimum impedance, crossover points, port tuning, bass extension and the voicing offsets are ESTIMATED or MODELLED rather than transcribed.',
  }),

  makeSpeaker({
    mfr: 'Odeon Audio',
    model: 'Rigoletto',
    price: usd(8500, 'APPROXIMATE', 2019, 'Approximate US retail per pair; Odeon prices in EUR and configurations vary by finish and horn material.'),
    year: 2010,
    country: 'Germany',
    speakerType: 'horn',
    enclosure: 'ported',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 96,
    nominalOhms: 8,
    minOhms: 6.0,
    bass: [38, 32],
    hfExtension: 20000,
    portTuningHz: 36,
    crossovers: [[900, 12]],
    responseTilt: [[20, 0], [60, 0.5], [150, 0.9], [400, -0.6], [900, 1.3], [2000, 0.4], [4000, -1.1], [8000, 0.6], [16000, -1.8]],
    resonances: [[175, -28, 8], [520, -33, 11]],
    coverageAngleDeg: 65,
    recommendedWatts: [5, 120],
    dimensionsMm: [1100, 380, 400],
    massKg: 32,
    thdPercent: 0.5,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#c9a227',
    tags: ['spherical-horn', 'two-way', 'set-friendly', 'reflex-bass'],
    notes:
      'A two-way with a large spherical horn from 900 Hz upward and a single reflex-loaded bass driver below it, and the interesting behaviour is exactly at the seam. Directivity changes abruptly where the horn takes over -- the woofer is radiating into close to a hemisphere at 900 Hz while the horn is already controlled to about 65 degrees -- so the on-axis response and the in-room power response pull apart by several decibels through that region. The voicing therefore carries a deliberate on-axis lift at the horn mouth with a dip above it, which is the honest shape of this kind of design rather than a flaw in the model.',
    provenanceNote:
      'Horn loading, two-way configuration and the high sensitivity typical of the range are from Odeon Audio published material. Sensitivity, crossover frequency, port tuning, bass extension, dimensions and the voicing offsets are ESTIMATED; Odeon publishes limited measured data and no distortion specification.',
  }),

  makeSpeaker({
    mfr: 'Blumenhofer Akustik',
    model: 'Genuin FS 3',
    price: usd(16000, 'APPROXIMATE', 2019, 'Approximate US retail per pair; priced in EUR at source and varies with finish.'),
    year: 2012,
    country: 'Germany',
    speakerType: 'horn',
    enclosure: 'ported',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 94,
    nominalOhms: 8,
    minOhms: 6.4,
    bass: [32, 27],
    hfExtension: 22000,
    portTuningHz: 30,
    crossovers: [{ freqHz: 1200, slopeDbOct: 12, alignment: 'butterworth', offsetMm: 40 }],
    responseTilt: [[20, 0], [70, -0.4], [200, 0.6], [600, -0.9], [1200, 0.5], [2500, 1.0], [5000, 0.2], [10000, -0.7], [20000, -1.5]],
    resonances: [[150, -31, 10], [470, -37, 14]],
    coverageAngleDeg: 70,
    recommendedWatts: [8, 150],
    dimensionsMm: [1180, 340, 520],
    massKg: 45,
    thdPercent: 0.45,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#a3937b',
    tags: ['horn-tweeter', 'adjustable-offset', 'butterworth-crossover'],
    notes:
      'The horn-loaded compression tweeter sits on an adjustable mount, so the acoustic offset between the two drivers is a user-set variable rather than a fixed design constant. That is recorded explicitly as a 40 mm driver offset in the crossover section, which the engine turns into an arrival-time error through the crossover region: change it and the summed response through 1.2 kHz changes shape without any driver having changed. The Butterworth alignment rather than Linkwitz-Riley means the two sections sum with a lobe rather than flat on axis, which is consistent with a design that expects to be aimed.',
    provenanceNote:
      'Horn-loaded tweeter, the adjustable tweeter position and the two-way reflex configuration are from Blumenhofer published material. Crossover frequency, slope, alignment, the 40 mm offset value, sensitivity, impedance and bass extension are ESTIMATED or MODELLED; the offset in particular is a plausible value, not a specification.',
  }),

  makeSpeaker({
    mfr: 'Voxativ',
    model: 'Ampeggio',
    price: usd(32000, 'APPROXIMATE', 2016, 'Approximate US retail per pair for the original Ampeggio with the AC-series driver.'),
    year: 2011,
    country: 'Germany',
    speakerType: 'horn',
    enclosure: 'horn-loaded',
    drivers: ['dynamic'],
    driverCount: 1,
    wayCount: 1,
    sensitivityDb: 100,
    nominalOhms: 8,
    minOhms: 6.5,
    bass: [38, 32],
    hfExtension: 20000,
    crossovers: [],
    responseTilt: [[20, 0], [45, 1.2], [80, 0.6], [200, -0.8], [800, -0.4], [2000, 0.8], [4000, 1.5], [8000, -0.9], [14000, -1.6], [20000, -2.0]],
    resonances: [[190, -29, 10], [430, -35, 13]],
    coverageAngleDeg: 45,
    recommendedWatts: [3, 60],
    dimensionsMm: [1080, 340, 520],
    massKg: 38,
    thdPercent: 0.6,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#b07d62',
    tags: ['single-driver', 'no-crossover', 'rear-horn', 'whizzer-cone'],
    notes:
      'One wide-band driver covering the entire audio range with no crossover anywhere in it: no phase rotation, no inter-driver arrival error, no network losses, and the profile carries zero crossover sections to say so. What replaces the crossover as the dominant character is the whizzer cone, whose controlled breakup produces the presence-region rise around 4 kHz and the beaming above it, modelled here as a 45 degree coverage angle -- narrower than anything else in this group. The folded rear horn adds output around 45 Hz with the group delay that a quarter-wave path implies, so its low end arrives later than a sealed box would.',
    provenanceNote:
      'Single wide-band driver operation, rear horn loading and the approximately 100 dB sensitivity are from Voxativ published material. Bass extension, impedance minimum, dimensions, mass, coverage angle and the voicing offsets are ESTIMATED or MODELLED. The specific driver variant fitted changes sensitivity and top-octave behaviour and is not distinguished here.',
  }),

  makeSpeaker({
    mfr: 'Rethm',
    model: 'Maarga',
    price: usd(9000, 'APPROXIMATE', 2019, 'Approximate US retail per pair; sold direct and through a small dealer network.'),
    year: 2014,
    country: 'India',
    speakerType: 'floorstander',
    enclosure: 'transmission-line',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 2,
    active: true,
    sensitivityDb: 94,
    nominalOhms: 8,
    minOhms: 6.0,
    bass: [30, 25],
    hfExtension: 19000,
    crossovers: [[120, 12]],
    responseTilt: [[20, 0], [50, 0.5], [120, -0.7], [400, 0.3], [1200, -0.5], [3000, 1.1], [6000, 0.2], [10000, -1.2], [18000, -2.0]],
    resonances: [[145, -27, 8], [340, -32, 10]],
    coverageAngleDeg: 70,
    recommendedWatts: [3, 50],
    dimensionsMm: [1000, 300, 450],
    massKg: 30,
    thdPercent: 0.5,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#7a9e7e',
    tags: ['powered-bass', 'tapered-line', 'wide-band-driver', 'hybrid-active'],
    notes:
      'Only the bass section is powered, and that split is what makes this profile behave unlike anything else here. Above 120 Hz a single wide-band driver works into a tapered quarter-wave line, and that is the only thing the external amplifier ever sees, so the amplifier match is governed by a benign 8 ohm wide-band load rather than by the woofer. Below 120 Hz an internal amplifier drives an isobaric pair, which means the low-frequency compression and distortion figures belong to that internal amplifier and are effectively fixed regardless of what the user puts in front of the speaker.',
    provenanceNote:
      'The powered bass module, tapered quarter-wave line and single wide-band driver above the bass crossover are from Rethm published material. Sensitivity, impedance, crossover frequency, bass extension, dimensions and the voicing offsets are ESTIMATED. Note that `active` is set true for the bass section only; the profile is a hybrid, not a fully active loudspeaker.',
  }),

  makeSpeaker({
    mfr: 'Charney Audio',
    model: 'Maestro',
    price: usd(9500, 'APPROXIMATE', 2021, 'Approximate US retail per pair. Final price depends heavily on the chosen wide-band driver; field-coil options cost substantially more.'),
    year: 2016,
    country: 'United States',
    speakerType: 'horn',
    enclosure: 'horn-loaded',
    drivers: ['field-coil'],
    driverCount: 1,
    wayCount: 1,
    sensitivityDb: 98,
    nominalOhms: 8,
    minOhms: 6.8,
    bass: [35, 28],
    hfExtension: 19000,
    crossovers: [],
    responseTilt: [[20, 0], [40, 0.9], [90, -1.0], [250, 0.4], [900, 0.6], [2500, -0.6], [5000, 1.3], [9000, 0.3], [15000, -1.4]],
    resonances: [[165, -30, 9], [390, -34, 12]],
    coverageAngleDeg: 50,
    recommendedWatts: [2, 50],
    dimensionsMm: [1120, 460, 600],
    massKg: 45,
    thdPercent: 0.55,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#8c6a4f',
    tags: ['tractrix-horn', 'field-coil', 'single-driver', 'no-crossover'],
    notes:
      'A true tractrix expansion rather than a folded exponential path, which gives a sharply defined mouth cutoff: the horn contributes usefully from roughly 40 Hz to 200 Hz and then hands over to direct radiation, and below cutoff output falls away far faster than the reflex and transmission-line designs in this group. That is why the impedance model here is the horn variant with a strong peak just above cutoff rather than a reflex saddle. This profile assumes the field-coil driver option: an electromagnet replaces the permanent magnet, so the flux does not modulate with voice-coil current and the magnetic hysteresis term of an AlNiCo or ferrite motor is absent from the distortion model.',
    provenanceNote:
      'Tractrix back-horn loading, single wide-band driver operation and the availability of field-coil driver options are from Charney Audio published material. Sensitivity, impedance, bass extension, dimensions, mass and the voicing offsets are ESTIMATED. Charney supplies the cabinet with several different drivers, so the numbers here describe one configuration rather than the model as a whole.',
  }),

  makeSpeaker({
    mfr: 'Cube Audio',
    model: 'Nenuphar',
    price: usd(12000, 'APPROXIMATE', 2021, 'Approximate US retail per pair; priced in EUR at source.'),
    year: 2018,
    country: 'Poland',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 1,
    wayCount: 1,
    sensitivityDb: 92,
    nominalOhms: 8,
    minOhms: 6.2,
    bass: [30, 25],
    hfExtension: 20000,
    portTuningHz: 29,
    crossovers: [],
    responseTilt: [[20, 0], [45, 0.4], [100, 0.8], [300, -0.5], [1000, 0.2], [2500, -0.9], [5000, 0.7], [9000, 1.2], [16000, -1.0]],
    resonances: [[140, -32, 11], [360, -38, 14]],
    coverageAngleDeg: 65,
    recommendedWatts: [4, 60],
    dimensionsMm: [1030, 380, 420],
    massKg: 40,
    thdPercent: 0.5,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#6f8faf',
    tags: ['single-driver', 'no-crossover', 'multi-whizzer', 'reflex-loaded'],
    notes:
      'The wide-band driver here uses several separate phase and whizzer cones rather than one, which splits what would be a single large breakup event into a set of smaller, higher-Q ones spread across the top two octaves. The voicing therefore carries a milder presence-region deviation than the Voxativ and Charney entries plus a small lift near 9 kHz, instead of one prominent 4 kHz peak followed by a steep beaming rolloff. The cabinet is a modest reflex rather than a horn, so at 92 dB this is the least sensitive design in the group and the sensitivity comes entirely from the motor, not from acoustic loading -- it is the only one of the four single-driver entries that a low-power single-ended amplifier will actually struggle with.',
    provenanceNote:
      'Multi-whizzer wide-band driver, single-driver crossoverless operation and reflex loading are from Cube Audio published material. Sensitivity, impedance, port tuning, bass extension, dimensions and the voicing offsets are ESTIMATED. The exact cabinet alignment is inferred from the described rear vent rather than from a published Thiele-Small alignment.',
  }),
];
